/**
 * Background work on an entity, stored at `processingData.tasks[slotKey]`
 * (a slot key is the kind, or `${kind}:${qualifier}`). The single legacy
 * `Artifact.processingData.task` (before PR 3b, 2026-10-02) is never written
 * fresh: `readBackgroundTasks` folds it into its kind's slot for readers, and
 * the backend's `adoptLegacyTask` moves it into the slot when a job needs to.
 *
 * Separate from `processingStatus`, which only says whether the BYTES exist
 * (uploading, ready, failed). A task is started on an entity whose bytes are
 * ready, so a failed task never touches `processingStatus` and never trips
 * HIDDEN_PROCESSING_STATUSES: a failed import stays visible.
 *
 * Written only by the backend background task service, which CAS-guards on
 * `requestedAt` and announces every write over /sync. Never polled.
 */
export const BACKGROUND_TASK_STATUS = {
    QUEUED: 'queued',
    RUNNING: 'running',
    READY: 'ready',
    FAILED: 'failed',
};
export const BACKGROUND_TASK_KIND = {
    SPREADSHEET_IMPORT: 'spreadsheet-import',
    NOTE_TRANSCRIPTION: 'note-transcription',
    NOTE_OCR: 'note-ocr',
    CONTENT_EXTRACTION: 'content-extraction',
    NOTE_FROM_FILE: 'note-from-file',
    /**
     * Used only by task:* notifications (slotKey `video-insight:<mode>`); no slot
     * is written for it. The state stays in `metadata.videoInsights[mode]`.
     */
    VIDEO_INSIGHT: 'video-insight',
};
/**
 * The Cloud Tasks queue each kind's job is dispatched on (spec section 7), for
 * the `lane` on `Background Task Completed` and the backend claim log's wait metric.
 */
export const TASK_LANE = {
    'note-transcription': 'interactive-processing',
    'note-ocr': 'interactive-processing',
    'content-extraction': 'artifact-processing',
    'note-from-file': 'artifact-processing',
    'spreadsheet-import': 'spreadsheet-import',
    'video-insight': 'video-insights',
};
const STATUSES = new Set(Object.values(BACKGROUND_TASK_STATUS));
const KINDS = new Set(Object.values(BACKGROUND_TASK_KIND));
function parseTask(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const t = raw;
    if (typeof t.kind !== 'string' || !KINDS.has(t.kind))
        return null;
    if (typeof t.status !== 'string' || !STATUSES.has(t.status))
        return null;
    if (typeof t.requestedAt !== 'string' || typeof t.updatedAt !== 'string')
        return null;
    return {
        kind: t.kind,
        status: t.status,
        requestedAt: t.requestedAt,
        updatedAt: t.updatedAt,
        ...(typeof t.errorCode === 'string' ? { errorCode: t.errorCode } : {}),
        ...(typeof t.requestedBy === 'string' ? { requestedBy: t.requestedBy } : {}),
        ...(t.targetArtifactType === 'protocol' ? { targetArtifactType: 'protocol' } : {}),
    };
}
export function readBackgroundTask(processingData) {
    if (!processingData || typeof processingData !== 'object')
        return null;
    return parseTask(processingData.task);
}
/**
 * A slot key: the kind, or `${kind}:${qualifier}`. The qualifier may hold an
 * underscore (a video insight mode such as `draft_protocol`); the kind may not.
 * The empty alternative spells `(:q)?` in a form security/detect-unsafe-regex
 * accepts. The reader
 * below and the `tasks` facet (schema/facets/background-tasks.ts) share it,
 * so the frontend never shows a key the facet would reject.
 */
export const BACKGROUND_TASK_SLOT_KEY = /^[a-z][a-z0-9-]*(:[a-z0-9_-]+|)$/;
/**
 * Every task on an entity, keyed by slot. The legacy `task` key is read as its
 * kind's slot; a `tasks` entry for the same slot wins (it is the newer home).
 * A `tasks` entry counts only under its own kind's key (`kind` or
 * `kind:qualifier`), so a junk key, another kind's key, or `__proto__` is
 * dropped. The result has a null prototype, so no key can alter it.
 */
export function readBackgroundTasks(processingData) {
    if (!processingData || typeof processingData !== 'object')
        return {};
    const out = Object.create(null);
    const legacy = readBackgroundTask(processingData);
    if (legacy)
        out[legacy.kind] = legacy;
    const slots = processingData.tasks;
    if (slots && typeof slots === 'object' && !Array.isArray(slots)) {
        for (const [slotKey, raw] of Object.entries(slots)) {
            const parsed = parseTask(raw);
            if (parsed &&
                BACKGROUND_TASK_SLOT_KEY.test(slotKey) &&
                (slotKey === parsed.kind || slotKey.startsWith(`${parsed.kind}:`))) {
                out[slotKey] = parsed;
            }
        }
    }
    return out;
}
/** Takes only the status, so a /sync task signal (kind + status) answers the same way. */
export function isBackgroundTaskActive(task) {
    return (task?.status === BACKGROUND_TASK_STATUS.QUEUED ||
        task?.status === BACKGROUND_TASK_STATUS.RUNNING);
}
/**
 * Busy outranks failed: a retry in flight is the newer fact, and a spinner
 * that resolves into an alert reads as progress (the STT tile's precedence,
 * frontend attachmentPresentation.ts getAttachmentStatus).
 */
const URGENCY = {
    queued: 2,
    running: 2,
    failed: 1,
    ready: 0,
};
export function mostUrgentTask(tasks) {
    let best = null;
    for (const t of tasks) {
        if (!best || URGENCY[t.status] > URGENCY[best.status])
            best = t;
    }
    return best;
}
//# sourceMappingURL=background-task.js.map